import { Link } from "react-router-dom";
import { MapPin, ArrowUpRight } from "lucide-react";
import Seo from "@/components/Seo";
import PageHero from "@/components/PageHero";
import PtfButton from "@/components/PtfButton";
import { Reveal, MaskText } from "@/components/motion/Reveal";
import { EvidenceChip } from "@/components/Evidence";
import { DAWN_CHALLENGES } from "@/lib/content";

const Eyebrow = ({ children, light }) => (
  <p className={`text-[0.7rem] uppercase tracking-[0.28em] mb-5 flex items-center gap-3 ${light ? "text-ptf-accent" : "text-ptf-primary/60"}`}>
    <span className={`h-px w-8 inline-block ${light ? "bg-ptf-accent" : "bg-ptf-primary/40"}`} />
    {children}
  </p>
);

const PROCESS = [
  { num: "01", title: "The project", text: "A project to understand rural tourism and develop communities living in remote locations." },
  { num: "02", title: "Adoption", text: "Three villages adopted in a remote location of Uttarakhand, to understand needs and meet the villagers' requirements." },
  { num: "03", title: "The mix", text: "Eleven SDGs mixed with outdoor and adventure sports to work toward the upliftment of the three adopted villages, as a pilot." },
  { num: "04", title: "Valley of River", text: "The area is introduced to the travel community as the Valley of River, inviting them to join curated trips: survival techniques, wilderness courses, river courses and treks, village tourism and volunteering in schools and villages." },
  { num: "05", title: "Revenue", text: "Income generated for the villages through homestays and local guiding for rural tourism." },
  { num: "06", title: "Livelihoods", text: "Improving villagers' and school children's prospects: new farming techniques, an introduction to adventure sports and digital literacy." },
];

const OUTCOME_PHOTOS = [
  { src: "/dawn/vor-medicines.jpg", caption: "Medicines and pads distributed" },
  { src: "/dawn/vor-computers.jpg", caption: "First computer access in nine years" },
  { src: "/dawn/vor-rafting.jpg", caption: "River programmes on the Ladhiya" },
  { src: "/dawn/vor-raft-build.jpg", caption: "Building a log raft with local hosts" },
  { src: "/dawn/vor-trek.jpg", caption: "70 km trekked to reach the villages" },
  { src: "/dawn/vor-school.jpg", caption: "Volunteering in the village school" },
];

const VILLAGES = [
  { name: "Furkiyajhala", coords: "29.2155,80.1439" },
  { name: "Khirdwari", coords: "29.2107,80.1634" },
  { name: "Lodiyalsera", coords: "29.2111,80.1701" },
];

export default function ValleyOfRiver() {
  return (
    <>
      <Seo title="Valley of River: Project Dawn" path="/dawn/valley-of-river" description="Valley of River is a documented Project Dawn rural tourism pilot: three adopted villages in the Ladhiya Valley, Uttarakhand, developed through responsible travel, training and community work." />
      <PageHero
        eyebrow="Project Dawn · Case study"
        titleLines={["Valley", "of River."]}
        intro="A rural tourism initiative in Uttarakhand: three villages adopted in the Ladhiya Valley, developed through responsible travel, adventure training and documented community work."
        image="/expeditions/valley-trekkers.jpg"
      />

      <section className="bg-ptf-bg py-16 md:py-24">
        <div className="mx-auto max-w-[1600px] px-5 sm:px-8 lg:px-12 grid grid-cols-1 lg:grid-cols-12 gap-12 items-start">
          <div className="lg:col-span-5">
            <Reveal><Eyebrow>The place</Eyebrow></Reveal>
            <MaskText
              lines={["Ladhiya Valley,", "Champawat."]}
              className="font-display font-extrabold uppercase text-4xl sm:text-5xl leading-[0.92] tracking-tight text-ptf-primary"
            />
            <Reveal delay={0.15}>
              <p className="mt-6 text-ptf-text/75 leading-relaxed">
                The valley sits in the Champawat district of Uttarakhand, reached via
                the Chalthi-Tanakpur road and then on foot: the field team trekked
                70 kilometres, crossing the Ladhiya river thirteen times, to reach the
                villages it would go on to adopt.
              </p>
              <div className="mt-6 flex flex-wrap gap-2" data-testid="vor-villages">
                {VILLAGES.map((v) => (
                  <a
                    key={v.name}
                    href={`https://maps.google.com/?q=${v.coords}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-1.5 text-[0.7rem] uppercase tracking-[0.12em] border border-ptf-primary/25 text-ptf-primary px-3 py-1.5 hover:bg-ptf-primary hover:text-ptf-bg transition-colors"
                  >
                    <MapPin className="h-3.5 w-3.5 text-ptf-secondary" /> {v.name}
                  </a>
                ))}
              </div>
              <p className="mt-3 text-[0.65rem] text-ptf-text/50">Documented GPS locations. Tap a village to open it on the map.</p>
              <div className="mt-5">
                <EvidenceChip status="DOCUMENTED" />
              </div>
            </Reveal>
          </div>
          <div className="lg:col-span-7 grid grid-cols-2 gap-4">
            <Reveal><img src="/dawn/vor-aerial.jpg" alt="Terraced fields of the valley from above" className="w-full aspect-[4/3] object-cover" loading="lazy" /></Reveal>
            <Reveal delay={0.08}><img src="/dawn/vor-village.jpg" alt="Stone houses of an adopted village" className="w-full aspect-[4/3] object-cover" loading="lazy" /></Reveal>
            <Reveal delay={0.12}><img src="/dawn/vor-farming.jpg" alt="A farmer ploughing with oxen" className="w-full aspect-[4/3] object-cover" loading="lazy" /></Reveal>
            <Reveal delay={0.16}><img src="/dawn/vor-family.jpg" alt="A family of the valley" className="w-full aspect-[4/3] object-cover" loading="lazy" /></Reveal>
          </div>
        </div>
      </section>

      <section className="bg-ptf-primary text-ptf-bg py-16 md:py-24">
        <div className="mx-auto max-w-[1600px] px-5 sm:px-8 lg:px-12">
          <Reveal><Eyebrow light>Documented problems</Eyebrow></Reveal>
          <h2 className="font-display font-extrabold uppercase text-3xl md:text-5xl tracking-tight leading-[0.95] max-w-3xl">What the villagers face.</h2>
          <p className="mt-5 text-ptf-bg/65 max-w-2xl leading-relaxed text-sm">Data gathered by adopting the three villages, recorded in the Project Dawn rural tourism report.</p>
          <div className="mt-10 grid grid-cols-2 md:grid-cols-5 gap-px bg-ptf-bg/10 border border-ptf-bg/10">
            {DAWN_CHALLENGES.map((c) => (
              <div key={c} className="bg-ptf-primary p-5">
                <p className="text-sm text-ptf-bg/85 leading-snug">{c}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-ptf-bg py-16 md:py-24">
        <div className="mx-auto max-w-[1600px] px-5 sm:px-8 lg:px-12">
          <div className="max-w-3xl mb-12">
            <Reveal><Eyebrow>The tourism model</Eyebrow></Reveal>
            <MaskText
              lines={["Six documented", "steps."]}
              className="font-display font-extrabold uppercase text-4xl sm:text-5xl leading-[0.92] tracking-tight text-ptf-primary"
            />
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-x-8 gap-y-10">
            {PROCESS.map((s, i) => (
              <Reveal key={s.num} delay={(i % 3) * 0.06}>
                <div className="border-t-2 border-ptf-primary/15 pt-5 h-full hover:border-ptf-accent transition-colors duration-300">
                  <span className="font-display text-3xl text-ptf-primary/30">{s.num}</span>
                  <h3 className="font-display uppercase text-xl tracking-tight text-ptf-primary mt-2 leading-none">{s.title}</h3>
                  <p className="mt-3 text-sm text-ptf-text/70 leading-relaxed">{s.text}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-white border-y border-ptf-primary/10 py-16 md:py-24">
        <div className="mx-auto max-w-[1600px] px-5 sm:px-8 lg:px-12">
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-12">
            <div>
              <Reveal><Eyebrow>Programmes conducted</Eyebrow></Reveal>
              <h2 className="font-display font-extrabold uppercase text-3xl md:text-5xl tracking-tight text-ptf-primary leading-[0.95]">The valley opened its doors.</h2>
            </div>
            <EvidenceChip status="DOCUMENTED" />
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {[
              { year: "2020", text: "The first Valley of River programme runs in December, before COVID. A small group treks, rafts and camps in the valley." },
              { year: "2021", text: "Programmes resume after lockdown, with groups of two, five and eight travellers hosted across the editions." },
              { year: "2023", text: "Programmes continue after 2023, alongside the Boom Rafting and Nandhaur Range wildlife expeditions developed for the area." },
            ].map((p, i) => (
              <Reveal key={p.year} delay={i * 0.07}>
                <div className="border border-ptf-primary/12 p-7 h-full">
                  <p className="font-display text-5xl text-ptf-primary/20">{p.year}</p>
                  <p className="mt-4 text-sm text-ptf-text/75 leading-relaxed">{p.text}</p>
                </div>
              </Reveal>
            ))}
          </div>
          <div className="mt-10 grid grid-cols-1 sm:grid-cols-3 gap-4">
            {[
              { src: "/dawn/vor-poster.jpg", caption: "Valley of River: trek, raft, camp (Dec 2020)" },
              { src: "/dawn/boom-poster.jpg", caption: "Boom Rafting Expedition · Planet Expeditions" },
              { src: "/dawn/nandhaur-poster.jpg", caption: "Nandhaur Range · Planet Wildlife" },
            ].map((p, i) => (
              <Reveal key={p.src} delay={i * 0.06}>
                <figure>
                  <img src={p.src} alt={p.caption} className="w-full aspect-[16/10] object-cover border border-ptf-primary/10" loading="lazy" />
                  <figcaption className="mt-2 text-[0.65rem] uppercase tracking-[0.12em] text-ptf-text/50">{p.caption}</figcaption>
                </figure>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-ptf-bg py-16 md:py-24">
        <div className="mx-auto max-w-[1600px] px-5 sm:px-8 lg:px-12">
          <div className="max-w-3xl mb-12">
            <Reveal><Eyebrow>Outcomes</Eyebrow></Reveal>
            <MaskText
              lines={["What the valley", "gained."]}
              className="font-display font-extrabold uppercase text-4xl sm:text-5xl leading-[0.92] tracking-tight text-ptf-primary"
            />
            <Reveal delay={0.15}>
              <p className="mt-5 text-ptf-text/70 leading-relaxed">
                Homestay revenue for local villagers, a 1.5 kW solar panel, a hand pump,
                repairs to the trolley, roller and generator, stationery, pads and
                medicines, each recorded in the field report and on the{" "}
                <Link to="/impact" className="underline underline-offset-2 text-ptf-primary">evidence page</Link>.
              </p>
            </Reveal>
          </div>
          <div className="grid grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6">
            {OUTCOME_PHOTOS.map((p, i) => (
              <Reveal key={p.src} delay={(i % 3) * 0.06}>
                <figure className="group">
                  <div className="aspect-[4/3] overflow-hidden">
                    <img src={p.src} alt={p.caption} className="h-full w-full object-cover transition-transform duration-[1.2s] group-hover:scale-105" loading="lazy" />
                  </div>
                  <figcaption className="mt-2 text-[0.65rem] uppercase tracking-[0.12em] text-ptf-text/55">{p.caption}</figcaption>
                </figure>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-ptf-secondary text-ptf-bg py-20 md:py-28">
        <div className="mx-auto max-w-[1600px] px-5 sm:px-8 lg:px-12 flex flex-col lg:flex-row lg:items-center lg:justify-between gap-8">
          <div>
            <h2 className="font-display font-extrabold uppercase text-4xl md:text-5xl tracking-tight leading-[0.95]">The valley is a beginning,<br />not a brochure.</h2>
            <p className="mt-4 text-ptf-bg/70 max-w-xl leading-relaxed text-sm">Current operational status of the pilot is being re-verified before new travel programmes are published for the area.</p>
          </div>
          <div className="flex flex-col sm:flex-row gap-3 shrink-0">
            <PtfButton to="/dawn" variant="accent">Back to Project Dawn</PtfButton>
            <PtfButton to="/partnerships" variant="outlineLight">Support the Next Phase</PtfButton>
          </div>
        </div>
      </section>
    </>
  );
}
